import React from "react";
import { Easing, interpolate, useCurrentFrame } from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { Tap } from "../../b2c/ui/bits";
import { fill, useCopy } from "../copy";
import { FLOAT_SHADOW, LOOK } from "../look";
import { useStage } from "../stage";
import { Headline, Kicker, Split, splitGeom, Sub } from "../ui/Split";
import { Sfx } from "../ui/Sfx";
import { usePop, Window } from "../ui/Surfaces";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Step 2 — your customers share a tracked link. The brand's own order
 * confirmation page with the Frak widget in it (the partner page's "use
 * case" asset, coded): the customer taps "Partager & gagner", gets a
 * personal link, and it leaves for WhatsApp, Instagram, SMS. The reward
 * is the 8 € set in step 1.
 */

const VISUAL = { w: 840, h: 680 };
const GAIN = 8;
/**
 * UI pacing in frames on purpose (how fast a hand taps and types), not
 * beats: only the scene length follows the music.
 */
const WIDGET_AT = 8;
const TAP_AT = 40;
const COPIED_AT = 46;
const CHANNELS_AT = 54;
/** Share button centre, inside the visual. */
const BUTTON = { x: 48 + 36 + 200, y: 590 };

const Check: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10.5" stroke={LOOK.blue} strokeWidth="1.6" />
    <path
      d="M7.5 12.4l3 3 6-6.4"
      stroke={LOOK.blue}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHANNEL_TINT = ["#25D366", "#E1306C", LOOK.blue];
/** Where each channel chip lands (window body px, top-left) + tilt. */
const CHANNEL_TO = [
  { x: 40, y: 30, r: -6 },
  { x: 590, y: 36, r: 5 },
  { x: 96, y: 118, r: 4 },
];

const Confirmation: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const s = copy.share;
  const gain = copy.money(GAIN);
  const widget = usePop(WIDGET_AT, true);
  const press = interpolate(
    frame,
    [TAP_AT - 2, TAP_AT + 1, TAP_AT + 6],
    [1, 0.94, 1],
    clamp,
  );
  const copied = usePop(COPIED_AT);
  const fly = interpolate(frame, [CHANNELS_AT, CHANNELS_AT + 18], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  return (
    <Window
      url={s.url}
      style={{ width: VISUAL.w, height: VISUAL.h, overflow: "visible" }}
      bodyStyle={{
        padding: "30px 48px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Check size={70} />
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 36,
          color: LOOK.ink,
          marginTop: 10,
        }}
      >
        {s.thanks}
      </div>
      <div
        style={{
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 28,
          color: LOOK.inkSoft,
          marginTop: 2,
        }}
      >
        {s.confirmed}
      </div>

      {/* The Frak widget. */}
      <div
        style={{
          alignSelf: "stretch",
          marginTop: 26,
          padding: "26px 36px 32px",
          borderRadius: 30,
          border: `2px solid ${LOOK.hairline}`,
          background: LOOK.white,
          position: "relative",
          opacity: widget,
          translate: `0 ${(1 - widget) * 60}px`,
          boxShadow: `0 0 0 ${interpolate(widget, [0, 1], [0, 8])}px rgba(0, 67, 239, ${0.12 * widget})`,
        }}
      >
        <div
          style={{
            display: "inline-block",
            padding: "6px 16px",
            borderRadius: 10,
            background: LOOK.sky,
            color: LOOK.blue,
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 22,
          }}
        >
          {s.badge}
        </div>
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 34,
            color: LOOK.ink,
            marginTop: 14,
            lineHeight: 1.2,
          }}
        >
          {fill(s.title, { gain })}
          <br />
          <span style={{ fontWeight: LOOK.semi }}>{s.line}</span>
        </div>
        <div
          style={{
            fontFamily: LOOK.body,
            fontSize: 24,
            color: LOOK.muted,
            marginTop: 10,
          }}
        >
          {s.small}
        </div>
        <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
          <div
            style={{
              padding: "20px 34px",
              borderRadius: 999,
              background: LOOK.ink,
              color: LOOK.white,
              fontFamily: LOOK.body,
              fontWeight: LOOK.bold,
              fontSize: 26,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              scale: press,
            }}
          >
            {fill(s.button, { gain })}
          </div>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FrakLogo width={96} />
          </div>
        </div>
      </div>

      {/* Channels the link leaves for: out of the button, into the empty
          corners around the "Merci" header, clear of the widget copy. */}
      {s.channels.map((ch, i) => {
        const to = CHANNEL_TO[i];
        return (
          <div
            key={ch}
            style={{
              position: "absolute",
              left: interpolate(fly, [0, 1], [BUTTON.x - 110, to.x]),
              top: interpolate(fly, [0, 1], [BUTTON.y - 64 - 30, to.y]),
              padding: "12px 24px",
              borderRadius: 999,
              background: CHANNEL_TINT[i],
              color: LOOK.white,
              fontFamily: LOOK.body,
              fontWeight: LOOK.bold,
              fontSize: 28,
              whiteSpace: "nowrap",
              boxShadow: FLOAT_SHADOW,
              opacity: fly > 0.01 ? 1 : 0,
              scale: String(0.4 + 0.6 * fly),
              rotate: `${to.r * fly}deg`,
            }}
          >
            {ch}
          </div>
        );
      })}

      {/* Toast: the personal link. */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: -34,
          translate: `-50% ${(1 - copied) * 30}px`,
          padding: "16px 30px",
          borderRadius: 999,
          background: LOOK.green,
          color: LOOK.white,
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: 28,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 12,
          boxShadow: FLOAT_SHADOW,
          opacity: copied,
        }}
      >
        🔗 {s.copied}
      </div>
    </Window>
  );
};

export const Share: React.FC = () => {
  const copy = useCopy();
  const stage = useStage();
  // Square keeps nearly tall's room: headline and sub both take two lines.
  const textH = { tall: 390, square: 390, wide: 400 }[stage.shape];
  const g = splitGeom(stage, VISUAL, textH);
  const origin = {
    x: g.visual.x + g.visual.w / 2 - (VISUAL.w / 2) * g.scale,
    y: g.visual.y + g.visual.h / 2 - (VISUAL.h / 2) * g.scale,
  };
  return (
    <>
      <Split
        background={LOOK.white}
        visualSize={VISUAL}
        textH={textH}
        text={
          <>
            <Kicker text={copy.share.kicker} step={2} />
            <Headline text={copy.share.headline} start={2} />
            <Sub text={copy.share.sub} at={24} />
          </>
        }
        visual={<Confirmation />}
      >
        <Tap
          x={origin.x + BUTTON.x * g.scale}
          y={origin.y + BUTTON.y * g.scale}
          at={TAP_AT}
          size={80}
        />
      </Split>
      <Sfx sound="pop" at={WIDGET_AT} />
      <Sfx sound="click" at={TAP_AT} />
      <Sfx sound="confirm" at={COPIED_AT} />
      {/* The link flies out to WhatsApp / SMS / Instagram. */}
      <Sfx sound="strike" at={CHANNELS_AT + 4} volume={0.8} />
    </>
  );
};
